import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { WidgetInterface } from '@modules/widgets-module/interfaces/widget.interface'
import { OrdersSettingsInterface } from '@shared/components/trading/widgets/orders-widget/core/interfaces/orders-settings.interface'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { OrdersResponseInterface } from '@modules/orders-module/interfaces/order-response.interface'
import { OptionOrderInterface } from '@modules/options-module/orders/interfaces/option-order.interface'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { OptionsOrdersService } from '@modules/options-module/orders/services/options-orders.service'
import { OptionsOrdersHelperService } from '@modules/options-module/orders/services/options-orders-helper.service'
import { ColumnsOptionsCanceledOrdersEnum } from '@shared/components/trading/widgets/orders-widget/core/enums/options/columns-options-canceled-orders.enum'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrderStatusEnum } from '@modules/orders-module/enums/order-status.enum'
import { TranslatePipe } from '@ngx-translate/core'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { DatePipe, UpperCasePipe } from '@angular/common'
import { TitleOrderTermPipe } from '@modules/orders-module/pipes/title-order-term.pipe'
import { TuiBadge, TuiSkeleton } from '@taiga-ui/kit'
import { OptionsCanceledOrdersActionsComponent } from '@shared/components/trading/orders/orders-options/tables/options/options-canceled-orders/common/options-canceled-orders-actions/options-canceled-orders-actions.component'
import { PaginationComponent } from '@shared/components/common/pagination/pagination.component'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { OrderParamsInterface } from '@modules/orders-module/interfaces/order-params.interface'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { SecretDirective } from '@shared/directives/secret.directive'
import { TableComponent } from '@shared/components/common/table/table.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-options-canceled-orders',
	templateUrl: './options-canceled-orders.component.html',
	styleUrl: './options-canceled-orders.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		PricePipe,
		TranslatePipe,
		QuantityPipe,
		HighlightDirective,
		UpperCasePipe,
		DatePipe,
		TitleOrderTermPipe,
		TuiBadge,
		OptionsCanceledOrdersActionsComponent,
		PaginationComponent,
		TableColumnComponent,
		TableActionsComponent,
		TableFooterComponent,
		SecretDirective,
		TuiSkeleton,
		TableComponent,
	],
})
export class OptionsCanceledOrdersComponent implements OnChanges, OnInit, OnDestroy {
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderStatusEnum: typeof OrderStatusEnum = OrderStatusEnum
	protected readonly optionOrderStateEnum: typeof OptionOrderStateEnum = OptionOrderStateEnum
	protected readonly columnsOptionsCanceledOrdersEnum: typeof ColumnsOptionsCanceledOrdersEnum =
		ColumnsOptionsCanceledOrdersEnum
	protected readonly pageSize: number = 30

	public widget: InputSignal<WidgetInterface<OrdersSettingsInterface> | null> =
		input<WidgetInterface<OrdersSettingsInterface> | null>(null)

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _optionsOrderService: OptionsOrdersService = inject<OptionsOrdersService>(OptionsOrdersService)
	private readonly _optionsOrderHelperService: OptionsOrdersHelperService =
		inject<OptionsOrdersHelperService>(OptionsOrdersHelperService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	protected page: number = 1

	protected ordersResponse: WritableSignal<OrdersResponseInterface<OptionOrderInterface> | null> =
		signal<OrdersResponseInterface<OptionOrderInterface> | null>(null)
	protected isLoading: WritableSignal<boolean> = signal<boolean>(false)
	protected ordersParams: WritableSignal<OrderParamsInterface<OptionOrderStateEnum> | undefined> = signal<
		OrderParamsInterface<OptionOrderStateEnum> | undefined
	>(undefined)
	protected needScrollToTop: WritableSignal<boolean> = signal<boolean>(false)
	protected statesColumns: WritableSignal<ColumnInterface<ColumnsOptionsCanceledOrdersEnum>[]> = signal<
		ColumnInterface<ColumnsOptionsCanceledOrdersEnum>[]
	>([])
	protected isSecret: WritableSignal<boolean> = signal<boolean>(false)
	private _listenSecret(): void {
		this._userHelperService.isSecret$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			this.isSecret.set(value)
		})
	}

	private _launchOptionsOrders(): void {
		this._tradingLauncherService
			.launchOptionsOrders$(this.optionOrderStateEnum.CANCELED)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				([ordersResponse, isLoading, ordersParams, isNeedUpdate]: [
					OrdersResponseInterface<OptionOrderInterface> | null,
					boolean,
					OrderParamsInterface<OptionOrderStateEnum> | undefined,
					boolean,
				]): void => {
					if (this.isLoading() !== isLoading) this.needScrollToTop.set(true)

					this.ordersResponse.set(ordersResponse)
					this.isLoading.set(isLoading)
					this.ordersParams.set(ordersParams)

					setTimeout((): void => this.needScrollToTop.set(false))
					if (isNeedUpdate) {
						this._optionsOrderHelperService.isNeedUpdateOptionsOrderResponseMap
							.get(this.optionOrderStateEnum.OPEN)
							?.next(false)
						this._getOrders()
					}
				},
			)
	}

	private _getOrders(): void {
		this._optionsOrderService
			.getOptionsOrders(this.optionOrderStateEnum.CANCELED)
			.pipe(takeUntil(this._destroy$))
			.subscribe()
	}

	private _updateStatesColumns(): void {
		const widget: WidgetInterface<OrdersSettingsInterface> | null = this.widget()
		if (!widget) return

		this.statesColumns.set([...widget.settings.options.orders.canceled.columns])
	}

	protected onChangePage(newPage: number): void {
		const ordersParams$: BehaviorSubject<OrderParamsInterface<OptionOrderStateEnum>> | undefined =
			this._optionsOrderHelperService.optionsOrdersParamsMap.get(this.optionOrderStateEnum.CANCELED)
		if (!ordersParams$) return

		ordersParams$.next({ ...ordersParams$.getValue(), page: newPage })

		this._getOrders()
		this.isLoading.set(true)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['widget']) this._updateStatesColumns()
	}

	ngOnInit(): void {
		this._listenSecret()
		this._launchOptionsOrders()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
